import { createContext, useContext, useState } from "react";
import { UserContext } from "./userProvider";
import apiUser from "../apiUser";

export const ContratosContext = createContext();

export const ContratosProvider = ({ children }) => {
    const { setAlert } = useContext(UserContext);
    const [contratos, setContratos] = useState([]);
    // Dono da lista atual. Zerar no fetch não basta: os efeitos dos consumidores
    // rodam com o closure do render anterior e ainda enxergam a lista velha —
    // o auto-skip precisa conferir a quem a lista pertence, não só o tamanho.
    const [clienteDosContratos, setClienteDosContratos] = useState(null);
    const [loading, setLoading] = useState(false);

    const aviso = (message, type = "warning") =>
        setAlert({ visible: true, title: type === "success" ? "Sucesso!" : "Atenção!", placeholder: message, confirm: false, type });

    const getContratosByClient = async (idCliente) => {
        try {
            setLoading(true);
            setContratos([]); // lista zerada no início do fetch — evita auto-skip com contratos stale de outro cliente
            setClienteDosContratos(null); // lista em trânsito não pertence a ninguém
            const { data } = await apiUser.get(`/contratos/cliente/${idCliente}`);
            if (data.success) {
                setContratos(data.dados);
                setClienteDosContratos(idCliente);
            } else aviso(data.message);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const createContrato = async (dados) => {
        try {
            const { data } = await apiUser.post("/contratos/create", dados);
            aviso(data.message, data.success ? "success" : "warning");
            if (data.success) getContratosByClient(dados.cliente_id);
            return data.success;
        } catch (error) { console.log(error); return false; }
    };

    const updateContrato = async (dados) => {
        try {
            const { data } = await apiUser.post("/contratos/update", dados);
            aviso(data.message, data.success ? "success" : "warning");
            if (data.success) getContratosByClient(dados.cliente_id);
            return data.success;
        } catch (error) { console.log(error); return false; }
    };

    const encerrarContrato = async (id, idCliente) => {
        try {
            const { data } = await apiUser.post("/contratos/encerrar", { id });
            aviso(data.message, data.success ? "success" : "warning");
            if (data.success) getContratosByClient(idCliente);
            return data.success;
        } catch (error) { console.log(error); return false; }
    };

    const reatribuirItens = async (idContrato, itens, idCliente) => {
        try {
            const { data } = await apiUser.post("/contratos/reatribuir-itens", { id_contrato: idContrato, itens });
            aviso(data.message, data.success ? "success" : "warning");
            if (data.success) getContratosByClient(idCliente);
            return { ok: data.success, avisos: data.dados?.avisos || [], message: data.message };
        } catch (error) { console.log(error); return { ok: false, avisos: [] }; }
    };

    const uploadArquivo = async (idContrato, file, idCliente) => {
        try {
            const form = new FormData();
            form.append("arquivo", file);
            const { data } = await apiUser.post(`/contratos/${idContrato}/arquivo`, form);
            aviso(data.message, data.success ? "success" : "warning");
            if (data.success) getContratosByClient(idCliente);
            return data.success;
        } catch (error) { console.log(error); return false; }
    };

    const downloadArquivo = async (contrato) => {
        try {
            const resp = await apiUser.get(`/contratos/${contrato.id}/arquivo`, { responseType: "blob" });
            if (resp.data && resp.data.type === "application/json") {
                const txt = JSON.parse(await resp.data.text());
                return aviso(txt.message);
            }
            const url = window.URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = contrato.arquivo_nome || "contrato.pdf";
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) { console.log(error); }
    };

    return (
        <ContratosContext.Provider value={{
            contratos, clienteDosContratos, loading, setContratos,
            getContratosByClient, createContrato, updateContrato,
            encerrarContrato, reatribuirItens, uploadArquivo, downloadArquivo,
        }}>
            {children}
        </ContratosContext.Provider>
    );
};
